import { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslationForArray } from '@monorepo/client-common';
import {
  DesignIcon,
  DesignIconNames,
  DesignIconsMap,
  SystemIcon,
} from '@monorepo/nova-icons';
import { Typography, useTheme } from '@monorepo/nova-ui';
import { HeaderMenuLink } from '@monorepo/types';
import { useMenuTranslationsQuery } from '../../../api/queries/use-menu-translations-query';
import { useConditionalLink, useGetEnvironment } from '../../../hooks';
import { MenuBanner } from './components';
import {
  StyledBannerContainer,
  StyledIconWrapper,
  StyledMenuDescription,
  StyledMenuInfoSection,
  StyledMenuItemLabel,
  StyledMenuItemRow,
  StyledMenuItemsContainer,
  StyledMenuTitle,
  StyledSubMenuContainer,
} from './styles/submenu-desktop.styled';

interface SubMenuDesktopProps {
  activeMenu: HeaderMenuLink | null;
  handleCloseSubMenu: (event?: MouseEvent | TouchEvent) => void;
}

export const SubMenuDesktop = forwardRef<HTMLDivElement, SubMenuDesktopProps>(
  ({ activeMenu, handleCloseSubMenu }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { is5ers } = useGetEnvironment();
    const [subMenuActiveId, setSubMenuActiveId] = useState<string | null>(null);
    const Link = useConditionalLink();
    const { data: menuTranslations } = useMenuTranslationsQuery();
    const i18nEntries = useMemo(
      () => menuTranslations?.['headerMenuLinks'] || [],
      [menuTranslations],
    );

    const getDesignIcon = (iconName?: string): DesignIconNames => {
      if (iconName && iconName in DesignIconsMap) {
        return iconName as DesignIconNames;
      }
      return 'HeaderRocket';
    };

    const iconName = getDesignIcon(activeMenu?.meta?.icon);

    const onHover = (id: string) => {
      setSubMenuActiveId(id);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCloseSubMenu();
      }
    };

    return (
      <StyledSubMenuContainer ref={ref}>
        <StyledMenuInfoSection>
          {iconName && (
            <StyledIconWrapper>
              <DesignIcon
                useMuiSvgIcon={false}
                iconName={iconName}
                className="rocket"
                sx={{
                  '& path': {
                    fill: `${theme.palette.primary.main} !important`,
                  },
                  '& circle': {
                    fill: `${theme.palette.primary.main} !important`,
                  },
                  '& rect': {
                    fill: `${theme.palette.primary.main} !important`,
                  },
                }}
              />
            </StyledIconWrapper>
          )}
          <Typography variant="h6" component={StyledMenuTitle}>
            {activeMenu?.meta?.label
              ? getDynamicTranslationForArray(
                  activeMenu.meta.label,
                  i18nEntries,
                  activeMenu.id,
                  'meta.label',
                  t,
                )
              : ''}
          </Typography>
          <Typography variant="body1" component={StyledMenuDescription}>
            {activeMenu?.meta?.description
              ? getDynamicTranslationForArray(
                  activeMenu.meta.description,
                  i18nEntries,
                  activeMenu.id,
                  'meta.description',
                  t,
                )
              : ''}
          </Typography>
        </StyledMenuInfoSection>
        <StyledMenuItemsContainer
          role="menu"
          aria-label={`${activeMenu?.meta?.label} submenu`}
        >
          {activeMenu?.children
            ?.filter((child) => child.active !== false)
            .map((child: HeaderMenuLink) => {
              const translatedChildLabel = getDynamicTranslationForArray(
                child.label,
                i18nEntries,
                child.id,
                'label',
                t,
              );

              return (
                <Link
                  href={child.href ?? ''}
                  key={`${child.label}-${child.id}`}
                  onMouseEnter={() => onHover(child.id)}
                  nextjs={child.nextjs}
                  {...(child.openInNewTab && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                  onClick={handleCloseSubMenu}
                  role="menuitem"
                  tabIndex={0}
                  onKeyDown={handleKeyDown}
                >
                  <StyledMenuItemRow isActive={subMenuActiveId === child.id}>
                    <Typography component={StyledMenuItemLabel}>
                      {translatedChildLabel}
                    </Typography>
                    <SystemIcon
                      iconName="ArrowRightMenu"
                      color={
                        subMenuActiveId === child.id
                          ? theme.palette.text.main
                          : theme.palette.text.third
                      }
                      viewBox="0 0 34 34"
                      size={34}
                    />
                  </StyledMenuItemRow>
                </Link>
              );
            })}
        </StyledMenuItemsContainer>
        <StyledBannerContainer>
          {is5ers && (
            <MenuBanner
              activeMenu={activeMenu}
              width={422}
              height="fit-content"
            />
          )}
        </StyledBannerContainer>
      </StyledSubMenuContainer>
    );
  },
);

SubMenuDesktop.displayName = 'SubMenuDesktop';

export default SubMenuDesktop;
